import { Button } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Skeleton } from '@delmonicos/shared-design-system-react/src/components/skeleton/skeleton';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { ChargePointOcppConfigurationUpdateController } from '@app/feature/charge-point/charge-point-ocpp-configuration-update/charge-point-ocpp-configuration-update.controller';
import {
  ChargePointOcppConfigurationContextProvider,
  useChargePointContext,
} from '@app/feature/charge-point/charge-point.context';
import { useCpmsBoGetChargePointOcppConfigurationQuery } from '@service/cpms-bo-charge-point-ocpp.service';

export const OcppConfigurationTabComponent = (): React.JSX.Element => {
  // CONTEXT

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canUpdateOcppConfiguration =
    !!userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.CHARGE_POINTS,
      action: ActionsByResource.CHARGE_POINTS.OCPP_UPDATE_CONFIGURATION,
    });

  // STATE /////////////////////////////////////////////////////////////////////

  const [isReadonly, setIsReadonly] = useState(true);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data, error, isFetching, isError, isSuccess } =
    useCpmsBoGetChargePointOcppConfigurationQuery({
      chargePointId: chargePoint.id,
    });

  // RENDER ////////////////////////////////////////////////////////////////////

  if (isFetching) {
    return (
      <Stack direction="column" gap={GapEnum.S}>
        <Skeleton width="50%" />
        <Skeleton />
        <Skeleton />
        <Skeleton width="50%" />
        <Skeleton />
        <Skeleton />
        <Skeleton width="50%" />
        <Skeleton />
        <Skeleton />
      </Stack>
    );
  }

  if (isError) {
    return <div>{JSON.stringify(error)}</div>;
  }

  if (isSuccess) {
    return (
      <Stack direction="column" gap={GapEnum.M}>
        <ChargePointOcppConfigurationContextProvider data={data}>
          <ChargePointOcppConfigurationUpdateController
            isReadonly={isReadonly}
            onCancel={() => setIsReadonly(true)}
            onSuccess={() => setIsReadonly(true)}
          />
        </ChargePointOcppConfigurationContextProvider>
        {isReadonly && canUpdateOcppConfiguration && (
          <Stack justifyContent="flex-end">
            <Button onClick={() => setIsReadonly(false)}>
              <Stack gap={GapEnum.S} alignItems="center">
                <Icon name="edit-05" />
                {t('global.edit')}
              </Stack>
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  return <div />;
};
